import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchSeedTrace,
  deleteSeedTraceItem,
  batchDeleteSeeds,
  type SeedTrace,
} from '../../lib/seedService';
import { logger } from '../../lib/logger';

function TraceSection({ title, icon, count, children }: {
  title: string;
  icon: string;
  count: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(count > 0);
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-lg">{icon}</span>
          <h3 className="font-semibold text-sm text-gray-900">{title}</h3>
          <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${count > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-400'}`}>
            {count}
          </span>
        </div>
        <span className="text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && count > 0 && (
        <div className="border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
}

const SeedTracesPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [trace, setTrace] = useState<SeedTrace | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const t = await fetchSeedTrace(userId);
      setTrace(t);
    } catch (err) {
      logger.error('load_seed_trace', 'SeedTracesPage', { error: String(err) });
      alert(`Erreur: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [userId]);

  const handleDeleteItem = async (table: string, id: string) => {
    setDeleting(id);
    try {
      await deleteSeedTraceItem(table, id);
      load();
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!userId || !confirm('Supprimer ce compte et TOUTES ses traces ? Cette action est irréversible.')) return;
    setLoading(true);
    try {
      await batchDeleteSeeds([userId]);
      alert('Compte et traces supprimés.');
      navigate('/command-board/accounts');
    } catch (err) {
      alert(`Erreur: ${err}`);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!trace) {
    return (
      <div className="text-center py-20 text-gray-400">
        Profil non trouvé
      </div>
    );
  }

  const { profile } = trace;
  const totalTraces =
    trace.videos.length +
    trace.interactions.length +
    trace.swipes.length +
    trace.favorites.length +
    trace.applications.length +
    trace.offers.length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/command-board/accounts')}
          className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
        >
          ← Retour aux comptes
        </button>
        <button
          onClick={handleDeleteAll}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
        >
          Supprimer tout ({totalTraces} traces)
        </button>
      </div>

      {/* Profile card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white text-lg font-bold ${profile.type === 'candidate' ? 'bg-indigo-500' : 'bg-purple-500'}`}>
            {profile.firstName?.[0]}{profile.lastName?.[0]}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900">{profile.firstName} {profile.lastName}</h1>
              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-indigo-100 text-indigo-700 uppercase">seed</span>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${profile.type === 'candidate' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                {profile.type === 'candidate' ? 'Candidat' : 'Recruteur'}
              </span>
            </div>
            <p className="text-sm text-gray-500">{profile.email}</p>
            <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
              <span>Campagne : <strong className="text-gray-600 font-mono">{profile.seedTag}</strong></span>
              <span>Ville : {profile.city || '—'}</span>
              <span>Créé le {profile.createdAt}</span>
            </div>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-indigo-600">{totalTraces}</p>
            <p className="text-xs text-gray-500">traces totales</p>
          </div>
        </div>
      </div>

      {/* Trace sections */}
      <TraceSection title="Vidéos" icon="🎬" count={trace.videos.length}>
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50"><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">ID</th><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Statut</th><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date</th><th className="px-4 py-2 text-right text-xs font-semibold text-gray-500"></th></tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {trace.videos.map((v) => (
              <tr key={v.id} className="hover:bg-gray-50/50">
                <td className="px-4 py-2 text-xs font-mono text-gray-500">{v.id.slice(0, 8)}...</td>
                <td className="px-4 py-2 text-xs">{v.status}</td>
                <td className="px-4 py-2 text-xs text-gray-400">{v.createdAt}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => handleDeleteItem('candidate_videos', v.id)} disabled={deleting === v.id} className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40">Supprimer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TraceSection>

      <TraceSection title="Interactions vidéo" icon="❤️" count={trace.interactions.length}>
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50"><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Vidéo</th><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Type</th><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {trace.interactions.map((i) => (
              <tr key={i.id} className="hover:bg-gray-50/50">
                <td className="px-4 py-2 text-xs font-mono text-gray-500">{i.videoId.slice(0, 8)}...</td>
                <td className="px-4 py-2 text-xs">{i.interaction}</td>
                <td className="px-4 py-2 text-xs text-gray-400">{i.createdAt}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => handleDeleteItem('video_interactions', i.id)} disabled={deleting === i.id} className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40">Supprimer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TraceSection>

      <TraceSection title="Swipes" icon="👆" count={trace.swipes.length}>
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50"><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Cible</th><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Action</th><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {trace.swipes.map((s) => (
              <tr key={s.id} className="hover:bg-gray-50/50">
                <td className="px-4 py-2 text-xs font-mono text-gray-500">{s.targetId.slice(0, 8)}...</td>
                <td className="px-4 py-2 text-xs">{s.action}</td>
                <td className="px-4 py-2 text-xs text-gray-400">{s.createdAt}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => handleDeleteItem('swipe_actions', s.id)} disabled={deleting === s.id} className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40">Supprimer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TraceSection>

      <TraceSection title="Favoris" icon="⭐" count={trace.favorites.length}>
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50"><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Type</th><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Cible</th><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {trace.favorites.map((f) => (
              <tr key={f.id} className="hover:bg-gray-50/50">
                <td className="px-4 py-2 text-xs">{f.targetType}</td>
                <td className="px-4 py-2 text-xs font-mono text-gray-500">{f.targetId.slice(0, 8)}...</td>
                <td className="px-4 py-2 text-xs text-gray-400">{f.createdAt}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => handleDeleteItem('favorites', f.id)} disabled={deleting === f.id} className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40">Supprimer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TraceSection>

      <TraceSection title="Candidatures" icon="📄" count={trace.applications.length}>
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50"><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Offre</th><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Statut</th><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {trace.applications.map((a) => (
              <tr key={a.id} className="hover:bg-gray-50/50">
                <td className="px-4 py-2 text-xs font-mono text-gray-500">{a.offerId.slice(0, 8)}...</td>
                <td className="px-4 py-2 text-xs">{a.status}</td>
                <td className="px-4 py-2 text-xs text-gray-400">{a.createdAt}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => handleDeleteItem('applications', a.id)} disabled={deleting === a.id} className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40">Supprimer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TraceSection>

      {trace.offers.length > 0 && (
        <TraceSection title="Offres (recruteur)" icon="📋" count={trace.offers.length}>
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50"><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Poste</th><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Ville</th><th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date</th><th className="px-4 py-2"></th></tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {trace.offers.map((o) => (
                <tr key={o.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-2 text-xs font-medium text-gray-900">{o.positionName}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{o.city}</td>
                  <td className="px-4 py-2 text-xs text-gray-400">{o.createdAt}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => handleDeleteItem('job_offers', o.id)} disabled={deleting === o.id} className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40">Supprimer</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TraceSection>
      )}
    </div>
  );
};

export default SeedTracesPage;
